import {
  ChangeDetectionStrategy,
  Component,
  inject,
  signal,
} from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router, RouterLink, RouterOutlet } from '@angular/router';
import { filter } from 'rxjs/operators';
import { TranslocoDirective, TranslocoService, provideTranslocoScope } from '@jsverse/transloco';
import { actionLabel, TbErrorViewComponent } from '@toybank/ui';
import { EntitlementCapabilityService } from './entitlement-capability.service';
import { CustomerSurfaceAccessService } from './customer-surface-access.service';
import { ENGINE_ACTION, routeEntitlement } from './entitlement-codes';
import { DemoPersonaSwitchService } from './demo/demo-persona-switch.service';
import { personaSwitchAppliesToHost } from './demo/persona-switch.model';
import { RecertInboxAccessService } from './features/recert-inbox/recert-inbox-access.service';

/** admin/entitlements/recert-inbox's own gate widening -- see {@link RecertInboxAccessService}. */
const RECERT_INBOX_ROUTE_PREFIX = 'admin/entitlements/recert-inbox';

/**
 * Route-wrapper around the single authenticated {@code <router-outlet />} (PBAC+ReBAC demo-persona
 * differentiation, Phase 3).
 *
 * It resolves the engine action code the active route requires — from the deepest activated route's
 * {@code data.entitlement}, falling back to the URL-keyed map in entitlement-codes.ts — and:
 *  - if the active persona HOLDS it (or the route is ungated, or this is a real user): projects the
 *    outlet unchanged.
 *  - if a DEMO persona LACKS it: renders an in-place "Not entitled to view this page" panel
 *    (tb-error-view, shield variant) — NEVER a redirect, so the demo stays on the URL and the
 *    learner sees the gate in context.
 *
 * IMPORTANT — demo-only: the in-place block fires ONLY for demo personas
 * ({@link DemoPersonaSwitchService} available + an active persona on a persona-switch host). Real
 * users ALWAYS get the outlet; the server's 403 handlers remain the real safety net. This is demo
 * UX, NOT a security control (the SPA never enforces auth).
 */
@Component({
  selector: 'tb-persona-gate-outlet',
  standalone: true,
  imports: [RouterOutlet, RouterLink, TranslocoDirective, TbErrorViewComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [provideTranslocoScope('personaGate')],
  template: `
    <ng-container *transloco="let t; read: 'personaGate'">
      @if (blocked()) {
        <tb-error-view
          mode="page"
          markVariant="shield"
          [title]="t('title')"
          [detail]="blockedDetail(t)"
          data-testid="persona-not-entitled"
        />
        <!-- WP-E section 4: name the missing permission (was a generic message that never said
             WHAT was missing) and link to the two natural remediation surfaces (was a dead end --
             no links at all). tb-error-view has no content-projection slot, so these live as a
             sibling block rather than nested inside it. -->
        <div class="persona-gate-actions" data-testid="persona-gate-actions">
          <a routerLink="/admin/entitlements/request-access" data-testid="persona-gate-request-access">
            {{ t('requestAccessLink') }}
          </a>
          <a routerLink="/admin/entitlements/my-authority" data-testid="persona-gate-self-view">
            {{ t('selfViewLink') }}
          </a>
        </div>
      }
      <!--
        The outlet is ALWAYS in the DOM (kept hidden when blocked) so route components still mount and
        the router state is consistent; only its visibility is toggled. Hiding (not *removing*) avoids a
        remount race with the same-URL persona-switch refresh.
      -->
      <div [hidden]="blocked()">
        <router-outlet />
      </div>
    </ng-container>
  `,
  styles: `
    .persona-gate-actions {
      display: flex;
      gap: 1rem;
      justify-content: center;
      margin: -0.5rem 0 1.5rem;
    }
    .persona-gate-actions a {
      color: var(--tb-accent, #4f86f7);
      text-decoration: underline;
      font-size: 0.9rem;
    }
  `,
})
export class PersonaGateOutletComponent {
  private readonly router = inject(Router);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly capabilities = inject(EntitlementCapabilityService);
  private readonly customerSurfaceAccess = inject(CustomerSurfaceAccessService);
  private readonly demoPersonaSwitch = inject(DemoPersonaSwitchService);
  private readonly i18n = inject(TranslocoService);
  private readonly recertInboxAccess = inject(RecertInboxAccessService);

  /** Whether this host is a persona-switch (demo) host at all. */
  private readonly personaHostApplies =
    typeof window !== 'undefined' && personaSwitchAppliesToHost(window.location.hostname);

  /** The engine code the current route requires (null = ungated). */
  private readonly routeCode = signal<string | null>(null);

  constructor() {
    // Resolve on first paint + every completed navigation.
    this.routeCode.set(this.resolveRouteCode());
    this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe(() => this.routeCode.set(this.resolveRouteCode()));
  }

  /**
   * Block the page IN PLACE only for a demo persona that lacks the route's entitlement. Reads the
   * reactive capability snapshot (this is a computed-style getter via signals) so it recomputes when
   * the persona switch refreshes the engine-fed capabilities.
   */
  readonly blocked = (): boolean => {
    if (!this.customerSurfaceAccess.canOpen(this.router.url)) return true;
    const code = this.routeCode();
    if (!code) return false; // ungated route — always show
    // Demo-personas ONLY: never fire for real users.
    if (!this.personaHostApplies) return false;
    if (!this.demoPersonaSwitch.available()) return false;
    if (this.demoPersonaSwitch.activePersona() == null) return false;
    if (this.capabilities.can(code)) return false;
    // F07: widen ONLY this one route -- a reviewer who holds >=1 assigned pending task may open
    // their own recert queue even without ENTITLEMENT_ADMIN. The inbox endpoint (scoped to the
    // ACTING subject) is the source of truth for that; every other admin/entitlements/* route
    // keeps requiring ENTITLEMENT_ADMIN untouched.
    if (code === ENGINE_ACTION.ENTITLEMENT_ADMIN
        && this.isRecertInboxRoute()
        && this.recertInboxAccess.hasPendingTask()) {
      return false;
    }
    return true;
  };

  private isRecertInboxRoute(): boolean {
    return this.router.url.split('?')[0].replace(/^\/+/, '').startsWith(RECERT_INBOX_ROUTE_PREFIX);
  }

  /**
   * WP-E section 4: was hardcoded English that never named the missing permission. Now translated
   * and names the actual engine action the route requires (via the shared actionLabel() map).
   */
  readonly blockedDetail = (t: (key: string, params?: Record<string, string>) => string): string => {
    if (!this.customerSurfaceAccess.canOpen(this.router.url)) return t('customerDetail');
    const persona = this.demoPersonaSwitch.activePersona();
    const who: string = persona?.label ?? this.i18n.translate<string>('personaGate.fallbackPersona');
    const code = this.routeCode();
    const action: string = code ? actionLabel(code) : this.i18n.translate<string>('personaGate.fallbackAction');
    return t('detail', { persona: who, action });
  };

  /**
   * Walk to the deepest activated route and read its {@code data.entitlement}; fall back to the
   * URL-keyed map (so routes without explicit data are still gated consistently with the nav).
   */
  private resolveRouteCode(): string | null {
    let route = this.activatedRoute.snapshot;
    while (route.firstChild) route = route.firstChild;
    const fromData = route.data?.['entitlement'];
    if (typeof fromData === 'string' && fromData) return fromData;
    return routeEntitlement(this.router.url);
  }
}
